import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CellColors } from '../../components/mini-crossword/MiniCrossword';
import { CrosswordInput, Direction, UsedCellData } from "../../types";

export type Guesses = {
  [direction: string]: {
    [number: string]: string[];
  };
};

export interface WordleSlice {
  guesses: {
    [index: number]: Guesses;
  },
  shareHistories: {
    [index: number]: CellColors[];
  },
  gameWins: {
    [index: number]: boolean;
  },
  lostCells: {
    [index: number]: UsedCellData;
  },
  times: {
    [index: number]: number;
  }
};

const initialState: WordleSlice = {
  guesses: {},
  shareHistories: {},
  gameWins: {},
  lostCells: {},
  times: {},
}

export const generateInitialGuessState = (data: CrosswordInput) => {
  return {
    across: Object.keys(data?.across || {}).reduce((guessData, num) => {
      guessData[num] = [];
      return guessData;
    }, {} as Guesses[string]),
    down: Object.keys(data?.down || {}).reduce((guessData, num) => {
      guessData[num] = [];
      return guessData;
    }, {} as Guesses[string]),
  }
};

export const normalizeGuesses = (data: CrosswordInput, raw?: Guesses): Guesses => {
  const next = generateInitialGuessState(data);
  if (!raw) return next;

  (['across', 'down'] as const).forEach((dir) => {
    Object.keys(next[dir]).forEach((num) => {
      const list = raw[dir]?.[num];
      const limit = data[dir]?.[num]?.answer?.length ?? 0;
      const cleaned = Array.isArray(list) ? list.filter((guess) => typeof guess === 'string') : [];
      next[dir][num] = limit > 0 ? cleaned.slice(0, limit) : cleaned;
    });
  });

  return next;
};

export const guessesForWord = (guesses: Guesses | undefined, direction: string, number: string) => {
  const list = guesses?.[direction]?.[number];
  return Array.isArray(list) ? list : [];
};

export const wordleSlice = createSlice({
  name: 'wordle',
  initialState,
  reducers: {
    addGuess: (state, action: PayloadAction<{ index: number, direction: Direction, number: string, guess: string, puzzle: CrosswordInput }>) => {
      const { index, direction, number, guess, puzzle } = action.payload;
      state.guesses[index] = normalizeGuesses(puzzle, state.guesses[index]);
      state.guesses[index][direction][number] ||= [];
      state.guesses[index][direction][number].push(guess);
    },
    pushShareHistory: (state, action: PayloadAction<{ index: number, cellColors: CellColors }>) => {
      const { index, cellColors } = action.payload;
      state.shareHistories[index] ||= [];
      state.shareHistories[index].push(cellColors);
    },
    setGameWon: (state, action: PayloadAction<{ index: number }>) => {
      const { index } = action.payload;
      state.gameWins[index] = true;
    },
    setLostCell: (state, action: PayloadAction<{ index: number, lostCell: UsedCellData }>) => {
      const { index, lostCell } = action.payload;
      state.lostCells[index] = lostCell;
    },
    resetState: (state, action: PayloadAction<{ index: number, puzzle: CrosswordInput }>) => {
      const { index, puzzle } = action.payload;
      state.guesses[index] = generateInitialGuessState(puzzle);
      state.shareHistories[index] = []
      delete state.gameWins[index];
      delete state.lostCells[index];
    },
    startTimer: (state, action: PayloadAction<{ index: number }>) => {
      const { index } = action.payload
      state.times[index] ||= 0;
    },
    incrementTimer: (state, action: PayloadAction<{ index: number }>) => {
      const { index } = action.payload
      if (state.times[index] === undefined) return;
      if (state.gameWins[index] || state.lostCells[index]) return;
      state.times[index] += 1;
    },
  },
});

export const { addGuess, pushShareHistory, setGameWon, setLostCell, startTimer, incrementTimer } = wordleSlice.actions;

export default wordleSlice.reducer;
